import React, { useState } from 'react';

interface SocialConnectionData {
  socialConnections: {
    facebook: boolean;
    instagram: boolean;
    tiktok: boolean;
    twitter: boolean;
    google: boolean;
    yelp: boolean;
    yelpUrl: string;
  };
}

interface SocialConnectionProps {
  data: SocialConnectionData;
  onUpdate: (data: Partial<SocialConnectionData>) => void;
  storeId: string;
}

interface SocialPlatform {
  id: string;
  name: string;
  icon: string;
  color: string;
  available: boolean;
  oauthReady: boolean;
  description: string;
  combined?: string;
}

const SOCIAL_PLATFORMS: SocialPlatform[] = [
  {
    id: 'facebook',
    name: 'Facebook & Instagram',
    icon: 'fab fa-facebook',
    color: '#1877F2',
    available: true,
    oauthReady: true,
    description: 'Manage Facebook and Instagram pages, post content, and track engagement',
    combined: 'instagram',
  },
  {
    id: 'google',
    name: 'Google Business',
    icon: 'fab fa-google',
    color: '#4285F4',
    available: true,
    oauthReady: true,
    description: 'Manage Google Business profile, respond to reviews, and update info',
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    icon: 'fab fa-tiktok',
    color: '#000000',
    available: true,
    oauthReady: false,
    description: 'Post video content, track views, and engage with your TikTok audience',
  },
  {
    id: 'twitter',
    name: 'X / Twitter',
    icon: 'fab fa-x-twitter',
    color: '#000000',
    available: true,
    oauthReady: false,
    description: 'Post updates, engage with customers, and monitor brand mentions',
  },
  {
    id: 'yelp',
    name: 'Yelp',
    icon: 'fab fa-yelp',
    color: '#D32323',
    available: true,
    oauthReady: false,
    description: 'Monitor and respond to Yelp reviews, track ratings and sentiment',
  },
];

export const SocialConnection: React.FC<SocialConnectionProps> = ({ data, onUpdate, storeId }) => {
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);

  const handleConnect = async (platformId: string) => {
    const platform = SOCIAL_PLATFORMS.find(p => p.id === platformId);
    if (!platform) return;

    if (platform.oauthReady) {
      setConnectingPlatform(platformId);

      // For Facebook/Instagram and Google, redirect to OAuth flow
      if (platformId === 'facebook') {
        // Trigger existing Facebook OAuth flow
        const updatedConnections = {
          ...data.socialConnections,
          facebook: true,
          instagram: true,
        };
        onUpdate({ socialConnections: updatedConnections });
      } else if (platformId === 'google') {
        const updatedConnections = {
          ...data.socialConnections,
          google: true,
        };
        onUpdate({ socialConnections: updatedConnections });
      }

      setConnectingPlatform(null);
    }
  };

  const handleDisconnect = (platformId: string) => {
    const updatedConnections = { ...data.socialConnections };
    if (platformId === 'facebook') {
      updatedConnections.facebook = false;
      updatedConnections.instagram = false;
    } else {
      (updatedConnections as Record<string, boolean | string>)[platformId] = false;
    }
    onUpdate({ socialConnections: updatedConnections });
  };

  const handleYelpUrlChange = (url: string) => {
    onUpdate({
      socialConnections: {
        ...data.socialConnections,
        yelpUrl: url,
      },
    });
  };

  const isConnected = (platformId: string): boolean => {
    return !!(data.socialConnections as Record<string, boolean | string>)[platformId];
  };

  return (
    <div>
      <h2>Social Media Connections</h2>
      <p className="wizard-step-description">
        Connect your social media accounts to manage posts, respond to reviews, and track
        engagement from a single dashboard. This step is optional -- you can connect later from settings.
      </p>

      <div className="pos-type-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
        {SOCIAL_PLATFORMS.map((platform) => {
          const connected = isConnected(platform.id);
          return (
            <div
              key={platform.id}
              className={`pos-type-card ${connected ? 'selected' : ''}`}
              style={{ cursor: 'pointer', position: 'relative' }}
            >
              {connected && (
                <div style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  background: '#22c55e',
                  color: 'white',
                  borderRadius: 12,
                  padding: '2px 8px',
                  fontSize: 11,
                  fontWeight: 600,
                }}>
                  Connected
                </div>
              )}
              {!platform.oauthReady && !connected && (
                <div className="pos-coming-soon-badge">Coming Soon</div>
              )}
              <div className="pos-type-card-icon" style={{ color: platform.color }}>
                <i className={platform.icon}></i>
              </div>
              <div className="pos-type-card-name">{platform.name}</div>
              <div className="pos-type-card-status" style={{ fontSize: 12 }}>
                {platform.description}
              </div>
              <div style={{ marginTop: 12 }}>
                {connected ? (
                  <button
                    className="wizard-btn wizard-btn-secondary"
                    style={{ fontSize: 12, padding: '4px 12px' }}
                    onClick={(e) => { e.stopPropagation(); handleDisconnect(platform.id); }}
                  >
                    Disconnect
                  </button>
                ) : platform.oauthReady ? (
                  <button
                    className="wizard-btn wizard-btn-primary"
                    style={{ fontSize: 12, padding: '4px 12px' }}
                    disabled={connectingPlatform === platform.id}
                    onClick={(e) => { e.stopPropagation(); handleConnect(platform.id); }}
                  >
                    {connectingPlatform === platform.id ? (
                      <><i className="fas fa-spinner fa-spin"></i> Connecting...</>
                    ) : (
                      <><i className="fas fa-plug"></i> Connect</>
                    )}
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Yelp URL manual entry */}
      <div style={{ marginTop: 24 }}>
        <div className="wizard-form-group">
          <label>Yelp Business URL</label>
          <input
            type="text"
            value={data.socialConnections.yelpUrl || ''}
            onChange={(e) => handleYelpUrlChange(e.target.value)}
            placeholder="https://www.yelp.com/biz/your-restaurant"
          />
          <small>
            Enter your Yelp business page URL to enable review monitoring. Full Yelp API access
            requires partner approval.
          </small>
        </div>
      </div>
    </div>
  );
};

export default SocialConnection;
